import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";

const AREAS = ["Downtown", "Kitsilano", "Mount Pleasant", "Gastown", "Yaletown", "West End", "Marpole", "East Van"];

export default function ComingSoon() {
  return (
    <div className="pt-32">
      <section className="px-5 sm:px-8 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-forest mb-6 flex items-center gap-2">
              <MapPin size={14} /> Now expanding
            </p>
          </Reveal>

          <h1 className="font-serif text-ink text-[3.2rem] leading-[0.98] sm:text-7xl lg:text-[6.5rem]">
            <MaskLines lines={["Coming soon to", "Metro Vancouver."]} delay={0.15} lineClass="italic" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 text-lg sm:text-xl text-ink/70 max-w-2xl leading-relaxed"
          >
            The vintage video guestbook is rolling into the heart of the city. Weddings, milestone birthdays, launches and every celebration in between — we're bringing the booth to Metro Vancouver. Be among the first to book your date.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }} className="inline-block">
              <Link to="/book" data-testid="coming-soon-book-cta" className="group rounded-full bg-forest text-cream px-8 py-4 text-base flex items-center gap-2 hover:bg-forest-deep transition-colors shadow-[0_14px_30px_-10px_rgba(26,79,54,0.6)]">
                Reserve your date early
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>
            <Link to="/packages" className="rounded-full border border-forest/30 text-forest px-8 py-4 text-base hover:bg-forest/5 transition-colors">
              See packages
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee text="Metro Vancouver · Coming soon" />

      <section className="px-5 sm:px-8 py-24">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <h2 className="font-sans font-extrabold tracking-tight text-4xl sm:text-5xl mb-6">Neighbourhoods on the list.</h2>
            <p className="text-ink/60 text-lg max-w-lg leading-relaxed">
              We already serve the wider Lower Mainland from our Brentwood, Burnaby base. Now we're deepening our roots right across the City of Vancouver — no venue too tucked away.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-x-6">
              {AREAS.map((a, i) => (
                <div key={a} className="border-b border-clubborder py-4 flex items-center gap-3">
                  <span className="font-serif text-forest/40 text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg">{a}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24">
        <div className="max-w-[1200px] mx-auto rounded-3xl bg-forest text-cream p-10 sm:p-16 text-center">
          <Reveal>
            <p className="font-serif italic text-4xl sm:text-5xl mb-6">Planning a Vancouver event?</p>
            <p className="text-cream/70 max-w-xl mx-auto mb-8">Send us your date now and you'll be first in line as we open the calendar for the city.</p>
            <Link to="/book" className="inline-block rounded-full bg-cream text-forest px-10 py-4 text-base font-medium hover:bg-sand transition-colors">
              Check Your Date
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
